import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Api } from '../application/panelApi';
import { Modal } from '../components/Modal';
import { ExportCsvModal } from '../components/ExportCsvModal';
import { LaneIcon } from '../components/LaneIcon';
import { NodePicks } from '../components/NodePicks';
import { toast } from '../components/Toast';
import {
  TasksChart,
  chartTotals,
  todayRateLabel,
  useTasksChart,
  type LiveStats,
} from '../features/charts';
import { tasksCsv, type CsvExport } from '../features/csvExport';
import {
  formatDelta,
  keywordClicks,
  keywordSearches,
  positionStats,
  type KeywordEntry,
  type PfTask,
} from '../domain/positions';
import {
  neuroBestQuery,
  neuroQueryStat,
  neuroRandomFromTask,
  neuroSelector,
  type NeuroQueryStat,
} from '../domain/neuro';
import {
  scenarioNames,
  scenariosForNeuro,
  type ScenarioLike,
} from '../domain/scenarioLanes';
import { errText, nf, plural } from '../lib/format';
import { DAYS, ST } from '../lib/icons';
import { clusterOccupancy, isLiveStatus } from '../features/clusters';
import { useClusterLive } from '../hooks/useClusterLive';
import { useApp } from '../store/AppContext';



export type TaskSched = {
  from: number;
  to: number;
  days: number[];
  weekend: number;
  night: boolean;
};

const schedKey = (taskId: string) => `bc_sched_${taskId}`;

export function loadSched(taskId?: string | null): TaskSched {
  const def: TaskSched = { from: 8, to: 23, days: [1, 1, 1, 1, 1, 1, 1], weekend: 70, night: true };
  if (!taskId) return { ...def };
  try {
    const raw = localStorage.getItem(schedKey(taskId));
    if (raw) return { ...def, ...(JSON.parse(raw) as Partial<TaskSched>) };
  } catch {

  }
  return { ...def };
}

export function saveSched(taskId: string | undefined, sched: TaskSched): void {
  if (!taskId) return;
  try {
    localStorage.setItem(schedKey(taskId), JSON.stringify(sched));
  } catch {

  }
}



type AutopilotLike = { status?: string; phase?: string } | null | undefined;

export function autopilotPhaseLabel(p: AutopilotLike): string {
  if (!p) return '—';
  if (p.status !== 'running') return 'Выключен';
  if (p.phase === 'farm') return 'Подготовка профилей';
  if (p.phase === 'target') return 'Работа по задачам';
  return 'Начало работы';
}

const stOf = (status: string): [string, string] => ST[status] ?? ['stop', status];

const SORTV: Record<string, (t: PfTask) => string | number> = {
  site: (t) => t.site,
  day: (t) => t.perDay || 0,
  queue: (t) => (t.clicksQueued || 0) + (t.clicksRunning || 0),
  res: (t) => t.clicksOk || 0,
  pos: (t) => (t.keywords || []).length,
  prog: (t) => t.progressPct || 0,
  date: (t) => t.createdAt || '',
};

const noop = () => undefined;

type TaskStatsShape = {
  total: number;
  active: number;
  plan: number;
  ok: number;
  failed: number;
  sent: number;
  miss: number;
  error?: number;
};



function confidenceTone(level: NeuroQueryStat['level']): string {
  if (level === 'confident') return 'var(--ok)';
  if (level === 'mixed') return 'var(--warn)';
  if (level === 'weak') return 'var(--err)';
  return 'var(--text-3)';
}

function KeysModal({ task, onClose }: { task: PfTask; onClose: () => void }) {
  const neuro = task.lane === 'neuro';
  const perDay = task.dailyClicks || task.perDay || 0;
  const baseline = Math.floor(Number(task.quotaBaselineOk) || 0);
  const doneToday = Math.max(0, (task.clicksOk || 0) - baseline) + (task.clicksRunning || 0);
  const share = perDay ? Math.min(100, Math.round((doneToday / perDay) * 100)) : 0;
  const rows = neuro ? (task.keywords || []).map(neuroQueryStat) : [];
  const confident = rows.filter((r) => r.level === 'confident');
  return (
    <Modal
      title={neuro ? `Запросы Copilot · ${task.site}` : `Ключевые запросы · ${task.site}`}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" data-close onClick={onClose}>
            Закрыть
          </button>
        </>
      }
    >
      <div className="note" style={{ marginBottom: 10 }}>
        {neuro ? (
          <>
            Сообщений в день: <b>{nf.format(perDay)}</b> · переходов на сайт сегодня{' '}
            <b>{nf.format(doneToday)}</b> ({share}% от плана)
            {confident.length ? (
              <>
                {' '}
                · Copilot уверенно показывает сайт по запросам:{' '}
                <b>{confident.map((r) => r.keyword).join(', ')}</b>
              </>
            ) : (
              ' · пока нет запросов, по которым Copilot уверенно показывает ваш сайт в ответе.'
            )}
          </>
        ) : (
          <>
            План кликов на сегодня: <b>{nf.format(perDay)}</b> · сделано сегодня{' '}
            <b>{nf.format(doneToday)}</b> ({share}% от плана)
          </>
        )}
      </div>
      <div className="scroll">
        <table className="keys-table">
          <thead>
            <tr>
              {neuro ? (
                <>
                  <th>Запрос в Copilot</th>
                  <th style={{ textAlign: 'right' }}>Сообщений</th>
                  <th style={{ textAlign: 'right' }}>Переходов</th>
                  <th style={{ textAlign: 'right' }}>Выдача сайта</th>
                  <th>Последний вопрос</th>
                </>
              ) : (
                <>
                  <th>Ключ</th>
                  <th style={{ textAlign: 'right' }}>Запросов</th>
                  <th style={{ textAlign: 'right' }}>Кликов</th>
                  <th style={{ textAlign: 'right' }}>
                    Позиция{' '}
                    <span className="hint" style={{ fontWeight: 400 }}>
                      (средняя)
                    </span>
                  </th>
                  <th>Запрос в Bing</th>
                  <th style={{ textAlign: 'right' }}>Δ 7 дней</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {neuro
              ? rows.map((r, i) => (
                  <tr key={`${r.keyword}:${i}`}>
                    <td data-label="Запрос" style={{ whiteSpace: 'normal' }}>{r.keyword}</td>
                    <td data-label="Сообщений" className="num">
                      <b>{nf.format(r.messages)}</b>
                      {r.running > 0 && (
                        <div className="dim" style={{ fontSize: 11 }}>
                          +{nf.format(r.running)} сейчас
                        </div>
                      )}
                    </td>
                    <td data-label="Переходов" className="num">
                      <b style={{ color: 'var(--ok)' }}>{nf.format(r.hits)}</b>
                    </td>
                    <td data-label="Выдача сайта" className="num">
                      <b style={{ color: confidenceTone(r.level) }}>
                        {r.messages ? `${Math.round(r.rate * 100)}%` : '—'}
                      </b>
                      <div className="dim" style={{ fontSize: 11 }}>
                        {r.label}
                      </div>
                    </td>
                    <td data-label="Последний вопрос" style={{ whiteSpace: 'normal', maxWidth: 280, color: 'var(--text-3)' }}>
                      {r.lastQuery || '—'}
                    </td>
                  </tr>
                ))
              : (task.keywords || []).map((k: KeywordEntry, i) => {
                  const stats = positionStats(k);
                  const running = Math.floor(Number(k.running) || 0);
                  return (
                    <tr key={`${k.keyword}:${i}`}>
                      <td data-label="Ключ" style={{ whiteSpace: 'normal' }}>{k.keyword}</td>
                      <td data-label="Запросов" className="num">
                        <b>{nf.format(keywordSearches(k))}</b>
                        {running > 0 && (
                          <div className="dim" style={{ fontSize: 11 }}>
                            +{nf.format(running)} сейчас
                          </div>
                        )}
                      </td>
                      <td data-label="Кликов" className="num">
                        <b style={{ color: 'var(--ok)' }}>{nf.format(keywordClicks(k))}</b>
                      </td>
                      <td data-label="Позиция" className="num">
                        <b>{stats.current === null ? '—' : nf.format(stats.current)}</b>
                      </td>
                      <td data-label="Запрос в Bing" style={{ whiteSpace: 'normal', maxWidth: 260, color: 'var(--text-3)' }}>
                        {k.lastQuery || k.keyword}
                      </td>
                      <td data-label="За 7 дней" className="num delta">{formatDelta(stats.delta)}</td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}



type KeyDraft = { id: number; keyword: string };

type TaskFormProps = {

  task: PfTask | null;
  mode: 'create' | 'edit' | 'clone';
  lane: 'pf' | 'neuro';
  proxyTags: string[];
  scenarios: ScenarioLike[];
  defaultEngine: 'bing' | 'google';
  onClose: () => void;
  onSaved: (id?: string) => Promise<void>;
  audit: (act: string, obj?: string) => void;
  extra?: ReactNode;
  footerStart?: ReactNode;
};

export function TaskFormModal({
  task,
  mode,
  lane,
  proxyTags,
  scenarios,
  defaultEngine,
  onClose,
  onSaved,
  audit,
  extra,
  footerStart,
}: TaskFormProps) {
  const t = task;
  const neuro = lane === 'neuro' || t?.lane === 'neuro';
  const tags = proxyTags.length ? proxyTags : ['DEFAULT'];
  const initEngine: 'bing' | 'google' =
    (t?.searchEngine || defaultEngine) === 'google' ? 'google' : 'bing';
  const seq = useRef(0);
  const nextId = () => {
    seq.current += 1;
    return seq.current;
  };

  const [site, setSite] = useState(t ? t.url || t.site : '');
  const [daily, setDaily] = useState(
    String(Math.max(1, Number(t?.dailyClicks || t?.perDay || 100) || 100)),
  );
  const engine = initEngine;
  const [scen, setScen] = useState(() => {
    if (neuro) {
      const neuroNames = scenariosForNeuro(scenarios).map((s) => s.name);
      if (t?.scenario && neuroNames.includes(t.scenario)) return t.scenario;
      return neuroNames[0] || '';
    }
    return t?.scenario || scenarioNames(scenarios, initEngine)[0] || '';
  });
  const [tag, setTag] = useState(t?.proxyTag || tags[0]);
  const [profile, setProfile] = useState<'warmed' | 'empty'>(
    t?.clickProfile === 'empty' ? 'empty' : 'warmed',
  );
  const [randomQueries, setRandomQueries] = useState(() =>
    neuro ? (t ? neuroRandomFromTask(t) : true) : false,
  );
  const [keys, setKeys] = useState<KeyDraft[]>(() => {
    const src = t?.keywords?.length ? t.keywords : [{ keyword: '' }];
    return src.map((k) => ({ id: nextId(), keyword: k.keyword }));
  });
  const [focusKey, setFocusKey] = useState<number | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const initSched = useMemo(() => loadSched(t?.id), [t?.id]);
  const [from, setFrom] = useState(String(initSched.from));
  const [to, setTo] = useState(String(initSched.to));
  const [weekend, setWeekend] = useState(String(initSched.weekend));
  const [days, setDays] = useState<number[]>(() => DAYS.map((_, i) => (initSched.days[i] ? 1 : 0)));
  const [night, setNight] = useState(Boolean(initSched.night));
  const [saving, setSaving] = useState(false);

  const neuroScenarios = useMemo(() => scenariosForNeuro(scenarios), [scenarios]);
  const names = useMemo(() => {
    if (neuro) {
      const list = neuroScenarios.map((s) => s.name);
      if (scen && !list.includes(scen)) list.unshift(scen);
      return list;
    }
    const list = scenarioNames(scenarios, engine);
    if (scen && !list.includes(scen)) list.unshift(scen);
    return list;
  }, [engine, neuro, neuroScenarios, scen, scenarios]);
  const neuroScen = neuroScenarios.find((s) => s.name === scen);

  const filled = keys.filter((k) => k.keyword.trim());
  const dailyN = Math.max(1, Math.floor(Number(daily) || 0) || 0);

  const stripCells = useMemo(() => {
    const f = Number(from);
    const tt = Number(to);
    return Array.from({ length: 24 }, (_, h) => {
      const on = f <= tt ? h >= f && h < tt : h >= f || h < tt;
      const dim = on && night && (h >= 23 || h < 6);
      return { h, cls: on ? (dim ? 'dim' : 'on') : '' };
    });
  }, [from, to, night]);

  const applyBulk = () => {
    const lines = bulkText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const i = line.lastIndexOf(';');
        return (i === -1 ? line : line.slice(0, i)).trim();
      })
      .filter(Boolean);
    if (!lines.length) {
      toast('Список пуст: вставьте хотя бы один ключ, по одному в строке.', 'err');
      return;
    }
    setKeys(lines.map((keyword) => ({ id: nextId(), keyword })));
    setBulkOpen(false);
    toast(
      neuro
        ? `Загружено запросов: ${lines.length}. Они заменили прежний список — сохраните задачу, чтобы применить изменения.`
        : `Загружено ключей: ${lines.length}. Они заменили прежний список — сохраните задачу, чтобы применить изменения.`,
      'ok',
    );
  };

  const save = async () => {
    const address = site.trim();
    if (!address) {
      toast(
        'Не удалось сохранить задачу: укажите адрес целевого сайта в разделе «Основное».',
        'err',
      );
      return;
    }
    let target: URL;
    try {
      target = new URL(address.includes('://') ? address : `https://${address}`);
      if (!['http:', 'https:'].includes(target.protocol) || target.username || target.password) {
        throw new Error('Invalid URL');
      }
    } catch {
      toast(
        'Не удалось сохранить задачу: адрес сайта указан неверно. Введите его в виде example.com или https://example.com/page.',
        'err',
      );
      return;
    }
    const hostname = target.hostname;
    const keywords = neuro
      ? (neuroScen?.queries || []).map((keyword) => ({ keyword, dailyClicks: 0 }))
      : filled.map((k) => ({ keyword: k.keyword.trim(), dailyClicks: 0 }));
    if (neuro && !scen.trim()) {
      toast(
        'Не удалось сохранить задачу: не выбран нейро-сценарий. Создайте его на странице «Сценарии» и выберите в поле «Нейро-сценарий».',
        'err',
      );
      return;
    }
    if (!keywords.length) {
      toast(
        neuro
          ? 'Не удалось сохранить задачу: в выбранном нейро-сценарии нет вопросов для Copilot. Добавьте их на странице «Сценарии».'
          : 'Не удалось сохранить задачу: добавьте хотя бы один ключ в разделе «Ключи».',
        'err',
      );
      return;
    }
    const dailyClicks = Math.max(1, Math.floor(Number(daily) || 0));
    if (!Number.isFinite(dailyClicks) || dailyClicks < 1) {
      toast(
        neuro
          ? 'Не удалось сохранить задачу: укажите, сколько сообщений в день отправлять в Copilot (не меньше 1).'
          : 'Не удалось сохранить задачу: укажите дневной план кликов на сайт (не меньше 1).',
        'err',
      );
      return;
    }
    const payload = {
      site: hostname,
      url: target.href,
      scenario: scen,
      proxyTag: tag,
      searchEngine: neuro ? 'bing' : engine,
      lane: neuro ? 'neuro' : 'pf',
      ...(neuro ? {} : { clickProfile: profile }),
      selector: neuro ? neuroSelector(randomQueries) : 'a',
      dailyClicks,
      keywords,
    };
    const sched: TaskSched = {
      from: Number(from),
      to: Number(to),
      days,
      weekend: Number(weekend),
      night,
    };
    setSaving(true);
    try {
      let savedId: string | undefined;
      if (mode === 'edit' && t) {
        await Api.tasks.update(t.id, payload);
        saveSched(t.id, sched);
        audit('Изменил задачу', hostname);
        savedId = t.id;
      } else {
        const r = await Api.tasks.create(payload);
        const newId: string | undefined = r?.task?.id;
        if (newId) saveSched(newId, sched);
        audit('Создал задачу', hostname);
        savedId = newId;
      }
      onClose();
      await onSaved(savedId);
      toast(
        mode === 'edit' ? 'Изменения в задаче сохранены.' : `Задача для ${hostname} создана.`,
        'ok',
      );
    } catch (err) {
      toast(errText(err), 'err');
    } finally {
      setSaving(false);
    }
  };

  const title = neuro
    ? mode === 'clone'
      ? 'Копия цели · AI поиск'
      : mode === 'edit'
        ? 'Настройки цели · AI поиск'
        : 'Новая цель · AI поиск'
    : mode === 'clone'
      ? 'Копия цели'
      : mode === 'edit'
        ? 'Настройки цели'
        : 'Новая цель · Поисковик';
  const saveLabel = mode === 'clone' ? 'Создать копию' : mode === 'edit' ? 'Сохранить' : 'Создать';

  return (
    <Modal
      title={title}
      wide
      onClose={onClose}
      footer={
        <>
          {footerStart}
          <button type="button" className="btn" data-close onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            id="mSave"
            disabled={saving}
            onClick={() => void save()}
          >
            {saveLabel}
          </button>
        </>
      }
    >
      <div className="tf-sec" data-pane="target">
        <h3 className="tf-sec-title">Цель</h3>
        <div className="field-row">
          <label htmlFor="mSite">{neuro ? 'Домен или URL сайта' : 'URL целевого сайта'}</label>
          <input
            type="text"
            id="mSite"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder={neuro ? 'Например: example.com' : 'Например: https://example.com/page'}
            spellCheck={false}
          />
        </div>
      </div>

      <div className="tf-sec" data-pane="keys">
        <h3 className="tf-sec-title">
          {neuro ? 'Запросы' : 'Ключи'}{' '}
          <span className="badge-n" id="mTabKeys">
            {neuro ? (neuroScen?.queries || []).length : filled.length}
          </span>
        </h3>
        {neuro ? (
          <>
            <div className="field-row">
              <label htmlFor="mScen">Нейро-сценарий</label>
              <select id="mScen" value={scen} onChange={(e) => setScen(e.target.value)}>
                {names.length ? (
                  names.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))
                ) : (
                  <option value="">Нет нейро-сценариев</option>
                )}
              </select>
            </div>
            <div className="scroll">
              <table className="keys-edit">
                <thead>
                  <tr>
                    <th>Запрос в Copilot</th>
                  </tr>
                </thead>
                <tbody>
                  {(neuroScen?.queries || []).length ? (
                    (neuroScen?.queries || []).map((q) => (
                      <tr key={q}>
                        <td style={{ whiteSpace: 'normal' }}>{q}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td>
                        <div className="empty">Вопросов нет</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="keys-sum">
              <span
                className={`keys-total mono${(neuroScen?.queries || []).length ? '' : ' warn'}`}
              >
                {(neuroScen?.queries || []).length
                  ? `${(neuroScen?.queries || []).length} ${plural((neuroScen?.queries || []).length, 'запрос', 'запроса', 'запросов')} · сообщений в день: ${nf.format(dailyN)}`
                  : 'Запросов нет: добавьте их в нейро-сценарий.'}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="scroll">
              <table className="keys-edit">
                <thead>
                  <tr>
                    <th>Ключевой запрос</th>
                    <th />
                  </tr>
                </thead>
                <tbody id="mKeyBody">
                  {keys.map((k) => (
                    <tr key={k.id}>
                      <td>
                        <input
                          type="text"
                          className="kw"
                          value={k.keyword}
                          placeholder="Например: купить пластиковые окна"
                          spellCheck={false}
                          autoFocus={k.id === focusKey}
                          onChange={(e) => {
                            const v = e.target.value;
                            setKeys((prev) =>
                              prev.map((x) => (x.id === k.id ? { ...x, keyword: v } : x)),
                            );
                          }}
                        />
                      </td>
                      <td style={{ width: 36 }}>
                        <button
                          className="icon-btn danger"
                          data-del
                          title="Убрать этот ключ из задачи."
                          type="button"
                          onClick={() => setKeys((prev) => prev.filter((x) => x.id !== k.id))}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          >
                            <path d="M6 6l12 12M18 6 6 18" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="keys-sum">
              <button
                type="button"
                className="btn btn-sm"
                id="mAddKey"
                onClick={() => {
                  const id = nextId();
                  setKeys((prev) => [...prev, { id, keyword: '' }]);
                  setFocusKey(id);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Добавить ключ
              </button>
              <button
                type="button"
                className="btn btn-sm"
                id="mBulk"
                onClick={() => setBulkOpen((v) => !v)}
              >
                Вставить списком
              </button>
              <span className={`keys-total mono${filled.length ? '' : ' warn'}`} id="mTotal">
                {filled.length
                  ? `${filled.length} ${plural(filled.length, 'ключ', 'ключа', 'ключей')} · кликов в день на сайт: ${nf.format(dailyN)}`
                  : 'Ключей нет: добавьте хотя бы один.'}
              </span>
            </div>
            <div className={`field-row${bulkOpen ? '' : ' hidden'}`} id="mBulkWrap">
              <label htmlFor="mKeys">Список ключей — по одному в строке</label>
              <textarea
                id="mKeys"
                rows={5}
                spellCheck={false}
                placeholder="По одному ключу в строке. Например: купить окна москва"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm"
                id="mBulkApply"
                style={{ alignSelf: 'flex-start' }}
                onClick={applyBulk}
              >
                Заменить ключи списком
              </button>
            </div>
          </>
        )}
      </div>

      <div className="tf-sec" data-pane="plan">
        <h3 className="tf-sec-title">Дневной план</h3>
        <div className="field-row">
          <label htmlFor="mDaily">
            {neuro ? 'Сообщений в Copilot / день' : 'Кликов / день (на этот URL)'}
          </label>
          <input
            type="number"
            id="mDaily"
            min={1}
            max={1000000}
            value={daily}
            onChange={(e) => setDaily(e.target.value)}
          />
        </div>
      </div>

      <div className="tf-sec" data-pane="how">
        <h3 className="tf-sec-title">{neuro ? 'Как отправлять' : 'Как кликать'}</h3>
        {neuro ? (
          <>
            <div className="field-row">
              <label htmlFor="mTag">Пул прокси</label>
              <select id="mTag" value={tag} onChange={(e) => setTag(e.target.value)}>
                {(tags.includes(tag) ? tags : [tag, ...tags]).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="grid2">
              <div className="field-row">
                <label htmlFor="mScen">Сценарий поведения</label>
                <select id="mScen" value={scen} onChange={(e) => setScen(e.target.value)}>
                  {names.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field-row">
                <label htmlFor="mTag">Пул прокси</label>
                <select id="mTag" value={tag} onChange={(e) => setTag(e.target.value)}>
                  {(tags.includes(tag) ? tags : [tag, ...tags]).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field-row">
              <label htmlFor="mProfile">Профиль для клика</label>
              <select
                id="mProfile"
                value={profile}
                onChange={(e) => setProfile(e.target.value === 'empty' ? 'empty' : 'warmed')}
              >
                <option value="warmed">Подготовленные профили</option>
                <option value="empty">Пустые профили</option>
              </select>
            </div>
          </>
        )}
      </div>

      <div className="tf-sec" data-pane="sched">
        <h3 className="tf-sec-title">Расписание</h3>
        <div className="grid3">
          <div className="field-row">
            <label htmlFor="mFrom">Старт, час</label>
            <input
              type="number"
              id="mFrom"
              min={0}
              max={23}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="field-row">
            <label htmlFor="mTo">Стоп, час</label>
            <input
              type="number"
              id="mTo"
              min={1}
              max={24}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="field-row">
            <label htmlFor="mWeekend">Норма в выходные, %</label>
            <input
              type="number"
              id="mWeekend"
              min={0}
              max={200}
              value={weekend}
              onChange={(e) => setWeekend(e.target.value)}
            />
          </div>
        </div>
        <div className="field-row">
          <label>Дни недели</label>
          <div className="days" id="mDays">
            {DAYS.map((d, i) => (
              <label className="day" key={d}>
                <input
                  type="checkbox"
                  data-d={i}
                  checked={Boolean(days[i])}
                  onChange={(e) =>
                    setDays((prev) =>
                      prev.map((v, j) => (j === i ? (e.target.checked ? 1 : 0) : v)),
                    )
                  }
                />
                <span>{d}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="field-row">
          <label>Профиль трафика по часам</label>
          <div className="strip" id="mStrip">
            {stripCells.map((c) => (
              <i key={c.h} className={c.cls} title={`${String(c.h).padStart(2, '0')}:00`} />
            ))}
          </div>
        </div>
      </div>

      <div className="tf-sec" data-pane="opts">
        <h3 className="tf-sec-title">Дополнительно</h3>
        <div className="tf-checks">
          {neuro ? (
            <div className="set-row">
              <div className="txt">
                <b>Случайный запрос из списка</b>
                <small>
                  Включено: для каждого сообщения берётся случайный вопрос из сценария. Выключено:
                  вопросы идут по порядку, по кругу, как в Поисковике.
                </small>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  id="mRandom"
                  checked={randomQueries}
                  onChange={(e) => setRandomQueries(e.target.checked)}
                />
                <i />
              </label>
            </div>
          ) : null}
          {extra}
          <div className="set-row">
            <div className="txt">
              <b>Ночной спад</b>
              <small>
                Ближе к ночи клики постепенно сокращаются, а не прекращаются резко в час остановки.
              </small>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                id="mNight"
                checked={night}
                onChange={(e) => setNight(e.target.checked)}
              />
              <i />
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}



export function TargetNodes() {
  const { clusters, settingsPayload, refreshSettings } = useApp();
  const remote: string[] = useMemo(() => {
    const ids = settingsPayload?.settings?.targetClusterIds;
    return Array.isArray(ids) ? (ids as string[]) : [];
  }, [settingsPayload]);
  const remoteKey = remote.join('|');
  const [ids, setIds] = useState<string[]>(remote);

  useEffect(() => {
    setIds(remote);
  }, [remoteKey]);

  const enabled = useMemo(
    () =>
      (clusters as Array<{ id: string; name: string; enabled?: boolean }>).filter((c) => c.enabled),
    [clusters],
  );

  const onChange = async (next: string[]) => {
    const prev = ids;
    setIds(next);
    try {
      const cur = (settingsPayload?.settings ?? {}) as Record<string, unknown>;
      await Api.settings.save({ settings: { ...cur, targetClusterIds: next } });
      toast(
        next.length
          ? `Сохранено: работа по задачам пойдёт только на выбранных нодах (${next.length}).`
          : 'Сохранено: работа по задачам пойдёт на всех доступных нодах.',
        'ok',
      );
      await refreshSettings().catch(noop);
    } catch (err) {
      setIds(prev);
      toast(errText(err), 'err');
    }
  };

  return (
    <NodePicks
      id="tgNodes"
      clusters={enabled}
      selectedIds={ids}
      onChange={(v) => void onChange(v)}
    />
  );
}



const STATUS_OPTIONS: Array<[string, string]> = [
  ['all', 'Все статусы'],
  ['run', 'Работают'],
  ['pause', 'Пауза'],
  ['stop', 'Остановлены'],
  ['not_found', 'Не найден'],
];

function FilterMenu({ id, label, value, options, onChange }: { id: string; label: string; value: string; options: Array<[string, string]>; onChange: (value: string) => void }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) ref.current?.removeAttribute('open');
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') ref.current?.removeAttribute('open');
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape); };
  }, []);
  return (
    <details className="st-filter-menu" ref={ref} id={id}>
      <summary aria-label={label}><span className="st-filter-value">{options.find(([key]) => key === value)?.[1]}</span></summary>
      <div className="st-filter-options" role="group" aria-label={label}>
        {options.map(([key, name]) => <button type="button" key={key} className={key === value ? 'active' : undefined} aria-pressed={key === value} onClick={() => { onChange(key); ref.current?.removeAttribute('open'); }}>{name}</button>)}
      </div>
    </details>
  );
}

export function TasksPage({ lane = 'pf' }: { lane?: 'pf' | 'neuro' }) {
  const {
    tasks: rawTasks,
    taskStats,
    ui,
    setUi,
    refreshTasks,
    refreshAutopilot,
    refreshClusters,
    setTaskStats,
    clusters,
  } = useApp();

  const tasks = rawTasks as PfTask[];
  const neuroPage = lane === 'neuro';
  const eng = ui.searchEngine === 'google' ? 'google' : 'bing';
  const rawStats = (taskStats ?? {}) as Partial<TaskStatsShape>;
  const s: TaskStatsShape = {
    total: rawStats.total ?? 0,
    active: rawStats.active ?? 0,
    plan: rawStats.plan ?? 0,
    ok: rawStats.ok ?? 0,
    failed: rawStats.failed ?? 0,
    sent: rawStats.sent ?? 0,
    miss: rawStats.miss ?? 0,
    error: rawStats.error,
  };

  const [showOk, setShowOk] = useState(true);
  const [showSent, setShowSent] = useState(true);
  const [keysFor, setKeysFor] = useState<PfTask | null>(null);
  const [exp, setExp] = useState<CsvExport | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const statsRef = useRef(taskStats);
  statsRef.current = taskStats;
  const onLiveStats = useCallback(
    (patch: LiveStats) => {
      setTaskStats({ ...(statsRef.current ?? {}), ...patch });
    },
    [setTaskStats],
  );
  const { points } = useTasksChart({ live: true, onStats: onLiveStats });
  useClusterLive();


  const clustersLoaded = useRef(clusters.length > 0);
  useEffect(() => {
    const tick = () => {
      void refreshTasks().catch(noop);
      void refreshAutopilot().catch(noop);
    };
    tick();
    if (!clustersLoaded.current) void refreshClusters().catch(noop);
    const timer = window.setInterval(tick, 10000);
    return () => window.clearInterval(timer);
  }, [refreshTasks, refreshAutopilot, refreshClusters]);


  const siteChoices = useMemo(
    () => tasks.filter((t) => (t.lane === 'neuro') === neuroPage && (neuroPage || (t.searchEngine || 'bing') === eng)),
    [tasks, eng, neuroPage],
  );
  const effectiveSiteId = siteChoices.some((t) => t.id === selectedSiteId) ? selectedSiteId : 'all';
  const filtered = useMemo(() => {
    const q = (ui.taskFilter || '').trim().toLowerCase();
    const rows = tasks.filter(
      (t) =>
        (t.lane === 'neuro') === neuroPage &&
        (neuroPage || (t.searchEngine || 'bing') === eng) &&
        (effectiveSiteId === 'all' || t.id === effectiveSiteId) &&
        (selectedStatus === 'all' || t.status === selectedStatus) &&
        (!q ||
          `${t.site} ${t.scenario} ${t.proxyTag} ${(t.keywords || []).map((k) => k.keyword).join(' ')}`
            .toLowerCase()
            .includes(q)),
    );
    const f = SORTV[ui.sortKey] || SORTV.site;
    const dir = ui.sortDir || 1;
    rows.sort((a, b) => {
      const x = f(a);
      const y = f(b);
      return (typeof x === 'string' ? x.localeCompare(String(y)) : x - (y as number)) * dir;
    });
    return rows;
  }, [tasks, eng, neuroPage, effectiveSiteId, selectedStatus, ui.taskFilter, ui.sortKey, ui.sortDir]);

  const total = filtered.length;
  const size = ui.pageSize || 25;
  const pages = Math.max(1, Math.ceil(total / size));
  const page = !ui.page || ui.page > pages ? 1 : ui.page;
  const from = (page - 1) * size;
  const view = filtered.slice(from, from + size);


  const neuroStats = useMemo(() => {
    const rows = tasks.filter((t) => t.lane === 'neuro');
    return rows.reduce(
      (acc, t) => {
        acc.total += 1;
        if (t.status === 'run') acc.active += 1;
        acc.plan += Number(t.dailyClicks || t.perDay || 0);
        acc.ok += t.clicksOk || 0;
        acc.okToday += Math.max(0, (t.clicksOk || 0) - Math.floor(Number(t.quotaBaselineOk) || 0));
        acc.failed += t.clicksFailed || 0;
        acc.sent += (t.clicksOk || 0) + (t.clicksFailed || 0);
        acc.miss += t.clicksMiss || 0;
        acc.error += t.clicksError || 0;
        return acc;
      },
      { total: 0, active: 0, plan: 0, ok: 0, okToday: 0, failed: 0, sent: 0, miss: 0, error: 0 },
    );
  }, [tasks]);

  const targetSummary = useMemo(() => {
    const rows = tasks.filter((t) => t.lane !== 'neuro');
    return rows.reduce(
      (acc, t) => {
        acc.total += 1;
        if (t.status === 'run') acc.active += 1;
        acc.plan += Number(t.dailyClicks || t.perDay || 0);
        acc.ok += t.clicksOk || 0;
        acc.okToday += Math.max(0, (t.clicksOk || 0) - Math.floor(Number(t.quotaBaselineOk) || 0));
        acc.miss += t.clicksMiss || 0;
        acc.error += t.clicksError ?? Math.max(0, (t.clicksFailed || 0) - (t.clicksMiss || 0));
        return acc;
      },
      { total: 0, active: 0, plan: 0, ok: 0, okToday: 0, miss: 0, error: 0 },
    );
  }, [tasks]);

  const fleetSummary = useMemo(() => {
    const enabledNodes = clusters.filter((c) => c.enabled !== false);
    const rows = enabledNodes.map((c) => {
      const occ = clusterOccupancy(c);
      const live = c.live ?? {};
      const online = isLiveStatus(c);
      const mode = live.workMode ?? (c.status === 'warming' || c.status === 'targeting' ? c.status : 'idle');
      const taskKind = !online
        ? 'Офлайн'
        : occ.farm > 0 && occ.target > 0
          ? `Фарм: ${occ.farm} · Таргеты: ${occ.target}`
          : occ.target > 0 || mode === 'targeting'
            ? `Таргеты (${occ.target || occ.running})`
            : occ.farm > 0 || mode === 'warming'
              ? `Фарм (${occ.farm || occ.running})`
              : occ.running > 0
                ? `В работе (${occ.running})`
                : 'Простой';
      return {
        id: c.id,
        name: c.name,
        online,
        running: occ.running,
        maxSlots: occ.cap,
        available: occ.available,
        farmNow: occ.farm,
        targetNow: occ.target,
        queued: occ.queued,
        taskKind,
      };
    });
    const totals = rows.reduce(
      (acc, r) => {
        if (r.online) acc.online += 1;
        acc.running += r.running;
        acc.maxSlots += r.maxSlots;
        acc.available += r.available;
        acc.farmNow += r.farmNow;
        acc.targetNow += r.targetNow;
        acc.queued += r.queued;
        return acc;
      },
      { online: 0, running: 0, maxSlots: 0, available: 0, farmNow: 0, targetNow: 0, queued: 0 },
    );
    return { rows, totals, totalNodes: enabledNodes.length };
  }, [clusters]);
  const viewStats = neuroPage ? neuroStats : s;
  const chartPoints = neuroPage
    ? points.map((p) => ({ ...p, sent: p.neuroSent, ok: p.neuroOk }))
    : points;
  const totals = chartTotals(chartPoints);
  const hasChartData = chartPoints.some((p) => p.sent > 0 || p.ok > 0);
  const rateLabel = todayRateLabel(chartPoints);
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayOk = chartPoints.find((p) => p.date === todayKey)?.ok ?? 0;
  const todaySent = chartPoints.find((p) => p.date === todayKey)?.sent ?? 0;
  const todayValue = neuroPage ? todaySent : todayOk;
  const todayPct = viewStats.plan ? Math.min(100, Math.round((todayValue / viewStats.plan) * 100)) : 0;
  const laneTasks = siteChoices;
  const laneCount = laneTasks.length;
  const attentionTasks = laneTasks.filter((t) => t.status === 'not_found');


  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (document.getElementById('modals')?.firstChild) return;
      const tag = (e.target instanceof HTMLElement ? e.target.tagName : '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === '/') {
        e.preventDefault();
        document.getElementById('taskFilter')?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <section id="page-tasks">
      <div className="st-page-head">
        <div>
          <h1 className="h1-lane">
            <LaneIcon kind={neuroPage ? 'ai' : 'search'} />
            {neuroPage ? 'AI поиск' : 'Поисковик'}
          </h1>
          <p>Процесс и результаты. Настройка задач находится в разделе «Запуск».</p>
        </div>
      </div>

      {attentionTasks.length > 0 ? (
        <section className="st-attention" aria-labelledby="st-attention-title">
          <h2 id="st-attention-title">Требуют внимания</h2>
          <ul>
            {attentionTasks.slice(0, 3).map((task) => (
              <li key={task.id}>
                <b>{task.site}</b>
                <span>Не найден в поиске</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section
        className="panel"
        id="dashboardSummary"
        style={{ padding: 16, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 10,
          }}
        >
          <div className="st-card" style={{ padding: 12, minWidth: 0 }}>
            <span className="sv-k">Таргет клики</span>
            <b style={{ fontSize: 22, display: 'block', marginTop: 4 }}>
              {nf.format(targetSummary.okToday)}{' '}
              <small style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>
                сегодня{targetSummary.plan > 0 ? ` из ${nf.format(targetSummary.plan)}` : ''}
              </small>
            </b>
            <p className="sv-facts" style={{ marginTop: 6 }}>
              <span>
                всего <b>{nf.format(targetSummary.ok)}</b>
              </span>
              <span>
                активных задач <b>{targetSummary.active}/{targetSummary.total}</b>
              </span>
            </p>
          </div>

          <div className="st-card" style={{ padding: 12, minWidth: 0 }}>
            <span className="sv-k">ИИ клики (Copilot)</span>
            <b style={{ fontSize: 22, display: 'block', marginTop: 4 }}>
              {nf.format(neuroStats.okToday)}{' '}
              <small style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>
                сегодня{neuroStats.plan > 0 ? ` из ${nf.format(neuroStats.plan)}` : ''}
              </small>
            </b>
            <p className="sv-facts" style={{ marginTop: 6 }}>
              <span>
                всего <b>{nf.format(neuroStats.ok)}</b>
              </span>
              <span>
                активных задач <b>{neuroStats.active}/{neuroStats.total}</b>
              </span>
            </p>
          </div>

          <div className="st-card" style={{ padding: 12, minWidth: 0 }}>
            <span className="sv-k">Браузеров запущено сейчас</span>
            <b style={{ fontSize: 22, display: 'block', marginTop: 4 }}>
              {nf.format(fleetSummary.totals.running)}{' '}
              {fleetSummary.totals.maxSlots > 0 ? (
                <small style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>
                  из {nf.format(fleetSummary.totals.maxSlots)}
                </small>
              ) : null}
            </b>
            <p className="sv-facts" style={{ marginTop: 6 }}>
              <span>
                Фарм: <b>{nf.format(fleetSummary.totals.farmNow)}</b>
              </span>
              <span>
                Таргеты: <b>{nf.format(fleetSummary.totals.targetNow)}</b>
              </span>
              {fleetSummary.totals.queued > 0 ? (
                <span>
                  очередь: <b>{nf.format(fleetSummary.totals.queued)}</b>
                </span>
              ) : null}
            </p>
          </div>

          <div className="st-card" style={{ padding: 12, minWidth: 0 }}>
            <span className="sv-k">Доступно для запуска</span>
            <b style={{ fontSize: 22, display: 'block', marginTop: 4, color: 'var(--ok)' }}>
              {nf.format(fleetSummary.totals.available)}{' '}
              <small style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-3)' }}>
                свободных слотов
              </small>
            </b>
            <p className="sv-facts" style={{ marginTop: 6 }}>
              <span>
                серверов онлайн <b>{fleetSummary.totals.online}/{fleetSummary.totalNodes}</b>
              </span>
            </p>
          </div>
        </div>

        {fleetSummary.rows.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 8,
            }}
          >
            {fleetSummary.rows.map((srv) => (
              <div
                key={srv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  flexWrap: 'wrap',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--surface-2, var(--surface))',
                  minWidth: 0,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <b style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {srv.name}
                  </b>
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                    Задача: <b>{srv.taskKind}</b>
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, fontSize: 12 }}>
                  <span>
                    запущено <b>{nf.format(srv.running)}</b>
                    {srv.maxSlots > 0 ? ` / ${nf.format(srv.maxSlots)}` : ''}
                  </span>
                  <span style={{ color: srv.available > 0 ? 'var(--ok)' : 'var(--text-3)' }}>
                    доступно ещё: <b>{nf.format(srv.available)}</b>
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <div className="st-top">
        <section className="st-card st-today">
          <span className="sv-k">{neuroPage ? 'Сообщения сегодня' : 'Клики сегодня'}</span>
          <b className="st-big" id="kpiPlan">
            {nf.format(todayValue)} {viewStats.plan > 0 ? <small>из {nf.format(viewStats.plan)}</small> : null}
          </b>
          {viewStats.plan > 0 ? <span className="lp-bar st-bar"><i style={{ width: `${todayPct}%` }} /></span> : null}
          {viewStats.plan > 0 || !neuroPage ? (
            <p className="sv-facts">
              {viewStats.plan > 0 ? <span>выполнено <b>{todayPct}%</b></span> : null}
              {!neuroPage ? <span id="kpiRate">темп <b>{rateLabel}</b></span> : null}
            </p>
          ) : null}
          {viewStats.plan > 0 ? <p className="st-metric-note">План всех задач, включая невыбранные в «Запуске».</p> : null}
        </section>

        <section className="st-card st-chart">
          <header className="st-chart-head">
            <h2>За 14 дней</h2>
            <div className="st-legend">
              <button
                type="button"
                id="serOk"
                className={`st-leg a${showOk ? ' on' : ''}`}
                aria-pressed={showOk}
                onClick={() => setShowOk((v) => !v)}
              >
                <i />
                {neuroPage ? 'Переходы из ИИ' : 'Успешные'}{' '}
                <b id="kpiOk">{nf.format(hasChartData ? totals.ok : 0)}</b>
              </button>
              <button
                type="button"
                id="serSent"
                className={`st-leg b${showSent ? ' on' : ''}`}
                aria-pressed={showSent}
                onClick={() => setShowSent((v) => !v)}
              >
                <i />
                {neuroPage ? 'Сообщения в Copilot' : 'Отправлено'}{' '}
                <b id="kpiSent">{nf.format(hasChartData ? totals.sent : 0)}</b>
              </button>
              {totals.sent ? (
                <span className="st-conv">
                  доходит <b>{Math.round((totals.ok / totals.sent) * 100)}%</b>
                </span>
              ) : null}
            </div>
          </header>
          <TasksChart points={chartPoints} showOk={showOk} showSent={showSent} />
        </section>
      </div>

      <div className="panel st-sites">
        <div className="st-sites-head">
          <h2>
            Сайты{' '}
            <span className="pf-count" id="taskCount">
              {total} из {laneCount}
            </span>
          </h2>
          <div className="st-tools">
            <input
              type="text"
              id="taskFilter"
              aria-label={neuroPage ? 'Поиск по сайту или запросу' : 'Поиск по сайту, ключу, сценарию или прокси'}
              placeholder={
                neuroPage
                  ? 'Поиск по сайту или запросу'
                  : 'Поиск по сайту, ключу, сценарию или прокси'
              }
              spellCheck={false}
              value={ui.taskFilter}
              onChange={(e) => setUi({ taskFilter: e.target.value, page: 1 })}
            />
            <FilterMenu id="siteFilter" label="Сайт" value={effectiveSiteId} options={[["all", "Все сайты"], ...siteChoices.map((t): [string, string] => [t.id, t.site])]} onChange={(value) => { setSelectedSiteId(value); setUi({ page: 1 }); }} />
            <FilterMenu id="statusFilter" label="Статус" value={selectedStatus} options={STATUS_OPTIONS} onChange={(value) => { setSelectedStatus(value); setUi({ page: 1 }); }} />
            <select
              id="taskSort"
              aria-label="Сортировка"
              value={ui.sortKey || 'site'}
              onChange={(e) =>
                setUi({
                  sortKey: e.target.value,
                  sortDir: e.target.value === 'site' ? 1 : -1,
                  page: 1,
                })
              }
            >
              <option value="site">По названию</option>
              <option value="day">По норме</option>
              <option value="res">По результату</option>
              <option value="prog">По прогрессу</option>
            </select>
            <button
              type="button"
              className="btn"
              data-export="tasks"
              onClick={() => setExp(tasksCsv(tasks))}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v11m-4-4 4 4 4-4M4 16v4h16v-4" /></svg>
              Экспорт CSV
            </button>
          </div>
        </div>

        <div className="st-list-head data-list-head" aria-hidden="true">
          <span>Сайт</span>
          <span>Сегодня</span>
          <span>За всё время</span>
          <span>{neuroPage ? 'Запросы' : 'Ключи'}</span>
          <span>Статус</span>
        </div>
        <ul className="st-list" id="taskBody">
          {view.length ? (
            view.map((t) => {
              const keys = t.keywords || [];
              const miss = t.clicksMiss ?? 0;
              const errors = t.clicksError ?? Math.max(0, (t.clicksFailed ?? 0) - miss);
              const perDay = Math.floor(Number(t.perDay || t.dailyClicks) || 0);
              const okToday = Math.max(
                0,
                (t.clicksOk || 0) - Math.floor(Number(t.quotaBaselineOk) || 0),
              );
              const pct = perDay ? Math.min(100, Math.round((okToday / perDay) * 100)) : 0;
              const [stCls, stLabel] = stOf(t.status);
              const best = neuroPage ? neuroBestQuery(t) : null;
              return (
                <li key={t.id} data-id={t.id} className={`st-row ${t.status}`}>
                  <div className="st-site">
                    <a href={t.url || '#'} target="_blank" rel="noopener noreferrer">
                      {t.site}
                    </a>
                    <small>
                      {neuroPage
                        ? `${t.scenario || 'Copilot'} · ${t.selector === 'random' ? 'случайный запрос' : 'по списку'}`
                        : t.scenario}
                      {` · прокси ${t.proxyTag}`}
                    </small>
                    {best ? (
                      <small className="st-best">
                        {best.level === 'confident' ? 'Уверенно выдаёт' : 'Лучший запрос'}:{' '}
                        {best.keyword}
                        {best.messages ? ` · ${Math.round(best.rate * 100)}%` : ''}
                      </small>
                    ) : null}
                  </div>
                  <div className="st-today-cell">
                    {neuroPage ? (
                      <span className="st-today-k"><b>{nf.format(okToday)}</b> переходов сегодня · лимит {nf.format(perDay)} сообщений</span>
                    ) : (
                      <>
                        <span className="st-today-k">сегодня <b>{nf.format(okToday)}</b> из {nf.format(perDay)}</span>
                        <span className="lp-bar st-row-bar"><i style={{ width: `${pct}%` }} /></span>
                      </>
                    )}
                  </div>
                  <div className="st-nums">
                    <span>
                      <b>{nf.format(t.clicksOk ?? 0)}</b> всего
                    </span>
                    <span>
                      <b className="warn">{nf.format(miss)}</b> промахов
                    </span>
                    <span>
                      <b className="err">{nf.format(errors)}</b> ошибок
                    </span>
                  </div>
                  <button
                    type="button"
                    className="st-keys"
                    data-act="keys"
                    onClick={() => setKeysFor(t)}
                  >
                    {keys.length}{' '}
                    {neuroPage
                      ? plural(keys.length, 'запрос', 'запроса', 'запросов')
                      : plural(keys.length, 'ключ', 'ключа', 'ключей')}
                  </button>
                  <span className={`pill ${stCls}`}>
                    <span className="led" />
                    {stLabel}
                  </span>
                </li>
              );
            })
          ) : (
            <li className="empty">{neuroPage ? 'Сайтов не найдено' : 'Целей не найдено'}</li>
          )}
        </ul>
        <div className="pager">
          <span className="pg-info" id="pgInfo">
            {total ? `${from + 1}–${Math.min(from + size, total)} из ${total}` : '0 из 0'}
          </span>
          <label className="pg-size">
            Строк
            <select
              id="pgSize"
              value={String(size)}
              onChange={(e) => setUi({ pageSize: Number(e.target.value), page: 1 })}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </label>
          <button
            type="button"
            className="btn btn-sm"
            id="pgPrev"
            disabled={page <= 1}
            onClick={() => setUi({ page: page - 1 })}
          >
            Назад
          </button>
          <button
            type="button"
            className="btn btn-sm"
            id="pgNext"
            disabled={page >= pages}
            onClick={() => setUi({ page: page + 1 })}
          >
            Вперёд
          </button>
        </div>
      </div>

      {keysFor && <KeysModal task={keysFor} onClose={() => setKeysFor(null)} />}

      {exp && <ExportCsvModal name={exp.name} csv={exp.csv} onClose={() => setExp(null)} />}
    </section>
  );
}
